import React, { useState, useEffect } from "react";

/**
 * Status icon matching the docs pattern:
 *   pending=circle, running=spinning, complete=check, error=cross
 */
function StatusIcon({ status }) {
  switch (status) {
    case "pending":
      return <span className="text-gray-400 text-sm">&#9675;</span>;
    case "running":
      return (
        <span className="inline-block animate-spin text-blue-500 text-sm">
          &#9684;
        </span>
      );
    case "complete":
      return <span className="text-green-500 text-sm">&#10003;</span>;
    case "error":
      return <span className="text-red-500 text-sm">&#10005;</span>;
    default:
      return null;
  }
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-gray-100 text-gray-600",
    running: "bg-blue-100 text-blue-700",
    complete: "bg-green-100 text-green-700",
    error: "bg-red-100 text-red-700",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${styles[status] || ""}`}
    >
      {status}
    </span>
  );
}

function getElapsedTime(startedAt, completedAt) {
  if (!startedAt) return null;
  const end = completedAt || Date.now();
  const seconds = Math.round((end - startedAt) / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/** Progress bar showing completed / total subagents. */
export function SubagentProgress({ subagents }) {
  const entries = Object.values(subagents);
  const completed = entries.filter(
    (s) => s.status === "complete" || s.status === "error"
  ).length;
  const total = entries.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px] text-gray-500">
        <span>Subagent progress</span>
        <span>
          {completed}/{total} complete
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-blue-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function SubagentCard({ subagent }) {
  const [expanded, setExpanded] = useState(true);
  const [elapsed, setElapsed] = useState(
    getElapsedTime(subagent.startedAt, subagent.completedAt)
  );

  // Live elapsed-time counter while the subagent is running
  useEffect(() => {
    if (subagent.status !== "running") {
      setElapsed(getElapsedTime(subagent.startedAt, subagent.completedAt));
      return;
    }
    const id = setInterval(() => {
      setElapsed(getElapsedTime(subagent.startedAt, null));
    }, 1000);
    return () => clearInterval(id);
  }, [subagent.status, subagent.startedAt, subagent.completedAt]);

  const displayContent =
    subagent.status === "complete" ? subagent.result : subagent.content;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 shadow-sm overflow-hidden">
      {/* Header / toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between p-3 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <StatusIcon status={subagent.status} />
          <div className="text-left">
            <h4 className="text-xs font-semibold capitalize text-gray-700">
              {subagent.name}
            </h4>
            {subagent.description && (
              <p className="text-[10px] text-gray-400 w-[80%]">
                {subagent.description}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {elapsed && (
            <span className="text-[10px] whitespace-nowrap text-gray-400">{elapsed}</span>
          )}
          <StatusBadge status={subagent.status} />
          <svg
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Collapsible body */}
      {expanded && displayContent && (
        <div className="border-t border-gray-200 px-3 py-2">
          <p className="text-xs text-gray-600 whitespace-pre-wrap ">
            {displayContent}
            {subagent.status === "running" && (
              <span className="inline-block h-3 w-0.5 ml-0.5 animate-pulse bg-blue-500 rounded" />
            )}
          </p>
        </div>
      )}
    </div>
  );
}
